import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import { useTheme } from "../../ThemeContext";
import { useFetchBudgetOverviewQuery } from "../../slice/logdetailSlice";
import { useBudgetTracking } from "../../hooks/useBudgetTracking";
import Loader from "../common/Loader";
import BudgetStatusCard, {
  SectionHeading,
  getBudgetCardColors,
} from "./BudgetStatusCard";

const getColors = getBudgetCardColors;

// Order + neutral/positive/negative styling mirror the API's
// `projects/budget/getBudgetOverview` response (see BudgetStatusCard.jsx).
const SUMMARY_ROW_ORDER = ["budget_used", "profit_loss", "change_requests", "original_budget_used"];

const mapSummaryToRows = (summary) => {
  if (!summary) return [];
  return SUMMARY_ROW_ORDER.filter((key) => summary[key]).map((key) => {
    const row = summary[key];
    let negative = false;
    let positive = false;
    if (key === "profit_loss") {
      positive = !!row.is_profit;
      negative = !row.is_profit;
    } else if (key === "change_requests") {
      positive = (row.value_raw || 0) > 0;
      negative = (row.value_raw || 0) < 0;
    }
    return { label: row.label, formula: row.formula, value: row.value, negative, positive };
  });
};

const EmployeeBudgetView = () => {
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const project_id = localStorage.getItem("project_id");

  // Read again every time the tab is opened: hours logged since the last visit change the figures,
  // and a cached answer (kept for a minute after leaving the tab) would still show the old ones.
  const { data: overview, isLoading: overviewLoading } = useFetchBudgetOverviewQuery(project_id, {
    skip: !project_id,
    refetchOnMountOrArgChange: true,
  });

  // Whether a budget % makes sense for this project (Fixed Cost only).
  const tracking = useBudgetTracking(project_id, overview, overviewLoading);

  if (overviewLoading) {
    return <Loader />;
  }

  const budgetStatus = overview?.budget_status;
  const contribution = overview?.my_contribution;
  const summaryRows = mapSummaryToRows(overview?.summary);

  return (
    <Box sx={{ width: "100%", py: 2, fontFamily: "inherit" }}>
      {/* Employees only ever see their share of logged hours here (no
          hours/cost-to-company toggle) — that toggle is manager/admin only,
          see ButgetTab.jsx. */}
      <BudgetStatusCard
        usedPct={budgetStatus?.budget_used_raw ?? 0}
        usedDisplay={budgetStatus?.budget_used}
        status={budgetStatus?.status ?? "—"}
        isOver={budgetStatus ? budgetStatus.status_key !== "healthy" : false}
        gaugeMax={budgetStatus?.gauge?.max ?? 150}
        gaugeMarker={budgetStatus?.gauge?.marker ?? 100}
        projectId={project_id}
        allowCostToggle={false}
        budgetType={tracking.budgetType}
        budgetTracked={tracking.budgetTracked}
        budgetTypeLoading={tracking.loading}
      />

      <SectionHeading colors={colors}>My contribution</SectionHeading>
      <Paper
        variant="outlined"
        sx={{ border: `1px solid ${colors.line2}`, borderRadius: "10px", overflow: "hidden", bgcolor: colors.paper }}
      >
        <Box sx={{ display: "flex", background: colors.head, fontWeight: 500, p: "10px 14px", borderBottom: `1px solid ${colors.line2}`, color: colors.ink }}>
          <Box sx={{ flex: 1 }}>Hours</Box>
          <Box sx={{ flex: 1, textAlign: "right" }}>Cost-to-company</Box>
        </Box>
        <Box sx={{ display: "flex", p: "10px 14px", borderBottom: `1px solid ${colors.line}`, color: colors.ink }}>
          <Box sx={{ flex: 1 }}>{contribution?.hours || "0h 00m"}</Box>
          <Box sx={{ flex: 1, textAlign: "right", fontWeight: 500 }}>{contribution?.cost_to_company || "₹0.00"}</Box>
        </Box>
        <Box sx={{ display: "flex", p: "10px 14px", color: colors.ink2, fontSize: 12 }}>
          <Box sx={{ flex: 1 }}>Period</Box>
          <Box sx={{ flex: 1, textAlign: "right" }}>{contribution?.period?.display || "—"}</Box>
        </Box>
      </Paper>

      <SectionHeading colors={colors}>Summary</SectionHeading>
      <Paper
        variant="outlined"
        sx={{ border: `1px solid ${colors.line2}`, borderRadius: "10px", overflow: "hidden", bgcolor: colors.paper }}
      >
        <Box sx={{ display: "flex", background: colors.head, fontWeight: 500, p: "10px 14px", borderBottom: `1px solid ${colors.line2}`, color: colors.ink }}>
          <Box sx={{ flex: 1 }}>Item</Box>
          <Box sx={{ width: 120, textAlign: "right" }}>Value</Box>
        </Box>
        {summaryRows.length === 0 && (
          <Box sx={{ p: "14px", textAlign: "center", color: colors.ink3, fontSize: 12.5 }}>
            No budget summary yet.
          </Box>
        )}
        {summaryRows.map((row, i) => (
          <Box
            key={row.label}
            sx={{
              display: "flex",
              alignItems: "center",
              p: "10px 14px",
              color: colors.ink,
              borderBottom: i === summaryRows.length - 1 ? "none" : `1px solid ${colors.line}`,
            }}
          >
            <Box sx={{ flex: 1 }}>
              {row.label}
              <Box component="span" sx={{ display: "block", fontSize: 11, color: colors.ink3, mt: "2px" }}>
                {row.formula}
              </Box>
            </Box>
            <Box
              sx={{
                width: 120,
                textAlign: "right",
                fontWeight: row.negative || row.positive ? 500 : 400,
                color: row.negative ? colors.red : row.positive ? colors.tealDeep : colors.ink,
              }}
            >
              {row.value}
            </Box>
          </Box>
        ))}
      </Paper>

      <Typography sx={{ fontSize: 11, color: colors.ink3, mt: 1.5 }}>
        Project amounts and other members' details are visible to managers only.
      </Typography>
    </Box>
  );
};

export default EmployeeBudgetView;
